import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * What a My Account screen shows while `AccountContext.userProfile` is still
 * undefined (#773 / #760).
 *
 * `AccountContext` declares `userProfile?: UserProfile` and initialises it as
 * `useState<UserProfile>()` (AccountContext.tsx:69 and :170) — i.e. it is
 * **undefined on first render of every account screen**, on every cold
 * navigation, and only becomes an object once `validateLogin()` resolves.
 * Four screens read through it unguarded and threw; combined with the
 * fallback-less error boundary (#774) that rendered a blank white page.
 *
 * Guarding alone would leave those screens rendering *nothing* for the
 * duration of the fetch — the same blank the customer was already getting,
 * just transient. So the guard renders this instead.
 *
 * New work, so MUI v9 (`@mui/material`) per the PM5 convert-on-touch rule.
 * The markup deliberately mirrors the `updating` spinner already used by
 * AutoRenewOn / AutoRenewOff / AddPhone, so a screen looks the same whether it
 * is waiting on the profile or on a mutation. Note the surface: My Account
 * renders on `#e8e8e8`, a LIGHT ground, which is why the copy is `ffNavy` and
 * not the app's usual light-on-dark (mui-conversion-guide.md §6).
 */
export default function ProfileLoading() {
  return (
    <Box sx={{ m: 5, textAlign: "center" }}>
      <CircularProgress variant="indeterminate" />
      <Typography
        variant="body2"
        sx={(theme) => ({ color: theme.palette.background.ffNavy })}
      >
        <br />
        Loading your account details...
      </Typography>
    </Box>
  );
}
